/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import { useTranslation } from "react-i18next";
import { Pencil1Icon } from '@radix-ui/react-icons';
import { Anchor, MobileIcon, PersonIcon, Text, UnstyledButton } from '@/shared/ui';
import { WhatsAppIcon } from '@/shared/ui/components/WhatsAppQRCode.parts';
import {
  InstagramGlyph, MessengerGlyph, INSTAGRAM_MAGENTA, MESSENGER_BLUE,
} from '@/shared/ui/components/basic/icons';
import { buildWhatsAppLink } from '@/shared/utils/whatsapp-link';
import type { ClientRow } from "../../context/ClientManagementContext";
import ClientAvatar from "../ClientAvatar";
import { SocialUnseenDot } from "../SocialUnseenDot";
import { getClientAvatarAccentColor } from "../../utils/avatar-accent";
import { resolvePhoneFallback, resolvePrimaryContact, resolveContactLink } from "../../utils/primary-contact";
import { fetchPortalConnection } from "../../services/send-social-message";
import { getContactLabel, isMessageSourced } from "../ClientProfilePanel/contact-helpers";
import { formatRelativeDay } from '@/shared/utils/date';
import { formatPhoneDisplay } from '@/shared/utils/format-phone';
import { hasUnseenSocialUpdate } from '@/shared/utils/social-lead-activity';
import { ListingTable, type ListingColumn } from "@/shared/ui/components/basic/listing-table";
import { EmptyState } from "@/shared/ui/components/basic/EmptyState";

interface ClientsTableViewProps {
  clients: ClientRow[];
  hasActiveQuery: boolean;
  onRowNavigate: (clientId: string) => void;
  onAddClient: () => void;
  onEditClient: (client: ClientRow) => void;
  t: (key: string) => string;
}

/**
 * Resolves the client's WhatsApp number.
 *
 * Only a contact explicitly typed `whatsapp` counts. A `phone` entry is NOT a
 * fallback: the contact model distinguishes the two, so presenting a landline
 * or a non-WhatsApp mobile behind a wa.me link would claim a channel the client
 * may not have.
 *
 * @param client - The client row.
 * @returns The WhatsApp number, or an empty string when the client has none.
 */
function resolveWhatsAppNumber(client: ClientRow): string {
  return (client.contacts ?? []).find((ct) => ct.type === 'whatsapp')?.value || '';
}

/**
 * Stably reorders clients so anyone with an unseen Social update floats to
 * the top of the (already paginated/filtered) list, without shuffling the
 * relative order otherwise.
 *
 * @param clients - The rows as loaded for the current page/filters.
 * @returns The same rows, unseen-first.
 */
function sortUnseenFirst(clients: ClientRow[]): ClientRow[] {
  const unseen = new Map(
    clients.map((c) => [c.client_id, hasUnseenSocialUpdate(c.client_id, c.lead_interactions)]),
  );
  return [...clients].sort((a, b) => Number(unseen.get(b.client_id)) - Number(unseen.get(a.client_id)));
}

/**
 * WhatsApp cell: a click-through wa.me link that does not trigger row
 * navigation, falling back to an em dash when no number is on file.
 *
 * @param props.client - The client row.
 * @param props.label - Accessible label for the link.
 * @returns The rendered cell.
 */
function WhatsAppCell({ client, label }: { client: ClientRow; label: string }) {
  const number = resolveWhatsAppNumber(client);
  if (!number) return <>—</>;
  return (
    <Anchor
      href={buildWhatsAppLink(number)}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`${label}: ${number}`}
      underline="hover"
      onClick={(e) => e.stopPropagation()}
      className="inline-flex items-center gap-(--space-1) text-[var(--status-success)]"
    >
      <WhatsAppIcon className="w-4 h-4" />
      {number}
    </Anchor>
  );
}

/**
 * A plain phone, as text: no link, since it is not known to be WhatsApp.
 *
 * @param props.number - The stored phone, or null.
 * @returns The rendered cell.
 */
function PhoneCell({ number }: { number: string | null }) {
  if (!number) return <>—</>;
  return (
    <Text component="span" className="inline-flex items-center gap-(--space-1)">
      <MobileIcon width={16} height={16} aria-hidden />
      {formatPhoneDisplay(number)}
    </Text>
  );
}

/**
 * Contacts cell: the client's highest-priority registered channel
 * (WhatsApp > Instagram > Facebook), else a plain phone as text, or an em
 * dash when they have none.
 *
 * WhatsApp shows the number behind a wa.me link. Instagram/Facebook ids are
 * opaque and page-scoped, so those show the Instagram @handle when known and
 * the platform name otherwise, linked per {@link resolveContactLink}.
 *
 * @param props.client - The client row.
 * @param props.whatsAppLabel - Accessible label for the WhatsApp link.
 * @param props.facebookPageId - Connected Page id, for the Business Suite inbox link.
 * @returns The rendered cell.
 */
function ContactCell({ client, whatsAppLabel, facebookPageId }: {
  client: ClientRow;
  whatsAppLabel: string;
  facebookPageId?: string | null;
}) {
  const contact = resolvePrimaryContact(client);
  if (!contact) return <PhoneCell number={resolvePhoneFallback(client)} />;
  if (contact.type === 'whatsapp') return <WhatsAppCell client={client} label={whatsAppLabel} />;

  const isInstagram = contact.type === 'instagram';
  const text = isInstagram && contact.handle ? `@${contact.handle}` : getContactLabel(contact.type);
  const href = resolveContactLink(client, contact, facebookPageId);
  const Glyph = isInstagram ? InstagramGlyph : MessengerGlyph;
  const color = isInstagram ? INSTAGRAM_MAGENTA : MESSENGER_BLUE;
  // The Messenger mark is drawn inset in its viewBox while WhatsApp's and
  // Instagram's fill it, so at the same size it reads smaller; scale it up
  // inside the same 16px slot so the three line up.
  const glyphSize = isInstagram ? 16 : 19;
  const content = (
    <>
      {/* The slot must not set its own colour: the glyphs paint with
          currentColor, which has to resolve to the link's brand colour. */}
      <Text
        component="span"
        className="inline-flex h-4 w-4 shrink-0 items-center justify-center"
        style={{ color: 'inherit' }}
      >
        <Glyph width={glyphSize} height={glyphSize} />
      </Text>
      {text}
    </>
  );
  const className = 'inline-flex items-center gap-(--space-1)';
  if (!href) {
    return <Text component="span" className={className} style={{ color }}>{content}</Text>;
  }
  return (
    <Anchor
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`${getContactLabel(contact.type)}: ${text}`}
      underline="hover"
      onClick={(e) => e.stopPropagation()}
      className={className}
      style={{ color }}
    >
      {content}
    </Anchor>
  );
}

/**
 * Tabular list of clients with an empty/no-results fallback.
 *
 * @param props - The rows, active-query flag, navigation/add callbacks, and translator.
 * @returns The rendered clients table or empty state.
 */
export function ClientsTableView({
  clients: unsortedClients, hasActiveQuery, onRowNavigate, onAddClient, onEditClient, t,
}: ClientsTableViewProps) {
  const { t: tLocal } = useTranslation('features/clients');
  const clients = useMemo(() => sortUnseenFirst(unsortedClients), [unsortedClients]);
  const { agency_id: agencyId } = useParams<{ agency_id: string }>();
  const needsPageId = useMemo(() => clients.some((c) => {
    const contact = resolvePrimaryContact(c);
    return contact?.type === 'facebook' && isMessageSourced(contact.source);
  }), [clients]);
  const [facebookPageId, setFacebookPageId] = useState<string | null>(null);

  useEffect(() => {
    if (!needsPageId || !agencyId) return undefined;
    let cancelled = false;
    fetchPortalConnection(agencyId)
      .then((info) => { if (!cancelled) setFacebookPageId(info.fb_page_id ?? null); })
      .catch(() => { /* no Page id: the Facebook contact just stays plain text */ });
    return () => { cancelled = true; };
  }, [needsPageId, agencyId]);

  if (clients.length === 0) {
    return (
      <EmptyState
        icon={<PersonIcon width={28} height={28} />}
        title={hasActiveQuery ? t('list.noResults.title') : t('list.empty.title')}
        description={hasActiveQuery ? t('list.noResults.description') : t('list.empty.description')}
        action={hasActiveQuery ? undefined : { label: t('list.empty.addClient'), onClick: onAddClient }}
      />
    );
  }

  // These headers are deliberately NOT `sortable`: the clients list is an
  // infinite-scroll, server-accumulated set (see useClientList), so
  // client-sorting the fetched rows would mis-order them against the rows not
  // yet loaded — the exact page-scoped sort anti-pattern. The only reorder
  // here is `sortUnseenFirst`, a business float-to-top, not a user sort.
  const columns: ListingColumn<ClientRow>[] = [
    { key: 'full_name', label: tLocal('list.headers.name'), width: '25%', render: (c) => (
      <div className="flex items-center gap-(--space-2-5)">
        <div className="relative shrink-0">
          <ClientAvatar fullName={c.full_name} size={32} accentColor={getClientAvatarAccentColor(c.full_name)} />
          <SocialUnseenDot show={hasUnseenSocialUpdate(c.client_id, c.lead_interactions)} />
        </div>
        <Text component="span" className="text-sm font-semibold text-[var(--text-primary)] truncate">
          {c.full_name}
        </Text>
      </div>
    )},
    { key: 'type_of_client', label: tLocal('list.headers.type'), width: '12%',
      render: (c) => tLocal(`list.types.${c.type_of_client}`) },
    { key: 'contacts', label: tLocal('list.headers.contacts'), width: '15%',
      render: (c) => (
        <ContactCell client={c} whatsAppLabel={tLocal('list.openWhatsApp')} facebookPageId={facebookPageId} />
      ) },
    { key: 'email', label: tLocal('list.headers.email'), width: '21%',
      render: (c) => c.email || '—' },
    { key: 'last_contact_date', label: tLocal('list.headers.lastContact'), width: '11%',
      render: (c) => formatRelativeDay(c.last_contact_date) || '—' },
    { key: 'actions', label: '', width: '9%', render: (c) => (
      <div className="flex items-center gap-(--space-1-5) justify-end">
        <UnstyledButton
          onClick={(e) => { e.stopPropagation(); onEditClient(c); }}
          aria-label={`${tLocal('list.edit')}: ${c.full_name}`}
          className="inline-flex items-center gap-(--space-1) px-(--space-2) py-(--space-1) text-xs font-semibold rounded border border-[var(--border-subtle)] text-[var(--text-secondary)] hover:bg-[var(--surface-secondary)] transition-colors"
        >
          <Pencil1Icon width={12} height={12} />
          {tLocal('list.edit')}
        </UnstyledButton>
        <UnstyledButton
          onClick={(e) => { e.stopPropagation(); onRowNavigate(c.client_id); }}
          className="px-(--space-2-5) py-(--space-1) text-xs font-semibold text-[var(--text-inverse)] rounded hover:opacity-90 transition-opacity"
          style={{ background: 'var(--seed-base)' }}
        >
          {tLocal('list.view')}
        </UnstyledButton>
      </div>
    )},
  ];

  const data = clients.map((r) => ({ ...r, id: r.client_id }));

  return (
    <>
      <div className="flex flex-col gap-(--space-2-5) sm:hidden">
        {clients.map((client) => (
          <div
            key={client.client_id}
            onClick={() => onRowNavigate(client.client_id)}
            className="flex items-center gap-(--space-3) px-(--space-3-5) py-(--space-3) rounded-(--radius-lg) border cursor-pointer active:bg-[var(--surface-secondary)] transition-colors border-[var(--border-subtle)] bg-[var(--surface-primary)]"
          >
            <div className="relative shrink-0">
              <ClientAvatar fullName={client.full_name} size={36} accentColor={getClientAvatarAccentColor(client.full_name)} />
              <SocialUnseenDot show={hasUnseenSocialUpdate(client.client_id, client.lead_interactions)} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-(--text-small) font-semibold text-[var(--text-primary)] leading-tight truncate">
                {client.full_name}
              </div>
              <div className="text-xs text-[var(--text-tertiary)] mt-(--space-0-5) flex items-center gap-(--space-1)">
                {/* This line is generic contact info rather than a WhatsApp
                    column, so a plain phone can stand in — as text, never as a
                    wa.me link, which would imply a channel the client lacks. */}
                {resolveWhatsAppNumber(client)
                  ? <WhatsAppCell client={client} label={tLocal('list.openWhatsApp')} />
                  : <Text component="span">{client.phone || '—'}</Text>}
                <Text component="span">· {tLocal(`list.types.${client.type_of_client}`)}</Text>
              </div>
            </div>
            <UnstyledButton
              onClick={(e) => { e.stopPropagation(); onEditClient(client); }}
              aria-label={`${tLocal('list.edit')}: ${client.full_name}`}
              className="shrink-0 p-(--space-1-5) rounded text-[var(--text-tertiary)] active:bg-[var(--surface-secondary)]"
            >
              <Pencil1Icon width={14} height={14} />
            </UnstyledButton>
          </div>
        ))}
      </div>

      <div className="hidden sm:block">
        <ListingTable
          data={data}
          columns={columns}
          onRowClick={(item) => onRowNavigate(item.client_id)}
          striped
          getRowProps={(item) => ({
            'data-testid': 'client-row', 'data-client-id': item.client_id,
          })}
        />
      </div>
    </>
  );
}
